import type { MetricKey } from 'helpers/statistics-glossary';
import { useEffect, useState } from 'react';
import { Select } from '@/components/Select';
import { TooltipProvider } from '@/components/ui/tooltip';
import { WorkspaceToolbarControls } from '../../../DashboardLayout/WorkspacePanel';
import { MetricLabel } from './MetricLabel';
import {
  buildCurrencyOptions,
  buildGoalOptions,
  CORRECTION_METHOD_OPTIONS,
  EVALUATION_METHOD_OPTIONS,
} from './control-options';
import { cn } from 'helpers/cn';

/**
 * The four controls that change what the page shows, pinned once their own cards
 * have scrolled away.
 *
 * The page is long enough that the objective, currency, method and correction are
 * off screen by the time you reach the numbers they govern, and changing any of them
 * meant scrolling back to the top to find out. This mirrors them rather than moving
 * them, so the cards stay where they are as the explained, primary place to set them.
 *
 * Visibility is driven by an IntersectionObserver rather than a scroll listener: the
 * browser reports the crossing itself, so there is no per-frame work and nothing to
 * throttle.
 *
 * `sticky`, not `fixed`. The bar used to be fixed to the viewport and to measure its
 * own left/width from an in-flow anchor, because the old shell never actually scrolled:
 * `SidebarInset` carried `overflow-auto` with only `min-h-svh`, so it was the nearest
 * scroll container for positioning while the window did the scrolling, and a sticky
 * descendant anchored to a box that never moved. The console shell is a fixed-height
 * grid and the workspace panel owns the only vertical scroll, so sticky resolves
 * against a box that really scrolls and the geometry comes from the flow for free — no
 * ResizeObserver, no column measuring, nothing to keep in step with the sidebar.
 *
 * The sticky wrapper is deliberately zero-height and lets the bar overflow it. The bar
 * is a mirror of controls that already exist further up the page, so it must cost no
 * layout space and must not push the report down while it is hidden; a zero-height
 * sticky box pins at the scrollport's top edge and the bar hangs off it, exactly where
 * the fixed version used to sit.
 *
 * It is always mounted and fades in, rather than being conditionally rendered, so the
 * reveal is a transition rather than a pop.
 */
type StickyControlBarProps = {
  manager: ReturnType<typeof import('@/state/experiments/useExperimentStats').useExperimentStats>;
  /** The element whose passing reveals the bar: the evaluation controls card. */
  watch: React.RefObject<HTMLElement | null>;
};

/**
 * The element that actually scrolls, used as the observer's root.
 *
 * With the viewport as root, "scrolled past" would be measured against `top < 0`, and
 * the workspace panel starts well below the viewport's top: the card would have to
 * leave the panel and then travel the height of the console header before the bar
 * appeared. Measuring against the panel's own top edge makes the reveal line up with
 * where the bar pins.
 */
const scrollParent = (node: HTMLElement): HTMLElement | null => {
  for (let parent = node.parentElement; parent; parent = parent.parentElement) {
    const { overflowY } = getComputedStyle(parent);
    if (overflowY === 'auto' || overflowY === 'scroll') return parent;
  }
  return null;
};

export const StickyControlBar = ({ manager, watch }: StickyControlBarProps) => {
  const {
    GOALS,
    goal,
    setGoal,
    goalLabels,
    currencies,
    currency,
    setCurrency,
    evaluationMethod,
    setEvaluationMethod,
    correctionMethod,
    setCorrectionMethod,
  } = manager;

  const [pinned, setPinned] = useState(false);

  useEffect(() => {
    // Effects run after the DOM is committed, so the watched div already exists.
    const target = watch.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      // Pinned exactly when the watched card has left the top of the scrolling panel.
      // Comparing against rootBounds.top distinguishes "scrolled past" from "not yet
      // reached", which a bare !isIntersecting cannot.
      ([entry]) => {
        if (!entry) return;
        const rootTop = entry.rootBounds?.top ?? 0;
        setPinned(!entry.isIntersecting && entry.boundingClientRect.top < rootTop);
      },
      { root: scrollParent(target), threshold: 0 },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [watch]);

  const isFrequentist = evaluationMethod === 'frequentist';
  const goalOptions = buildGoalOptions(GOALS, goalLabels);
  const currencyOptions = buildCurrencyOptions(currencies ?? []);

  return (
    <WorkspaceToolbarControls>
      <TooltipProvider>
        {/*
          `inert`, not `aria-hidden`.
          `aria-hidden` hid these four selects from assistive tech but left them in
          the tab order, so while the bar was unpinned a keyboard user hit four
          invisible, undescribable stops at the top of the tab. `inert` removes them
          from both the tab order and the accessibility tree, which is what was
          meant, and it keeps the fade.
        */}
        {/*
          Unpinned, the selects take no room at all (`hidden`), not only no visibility: the header's
          Start / End and menu share this toolbar (decided 2026-10-20), and four invisible selects
          held about 550px of it, enough to push End and the menu past the clipped edge. Pinned and
          short of room, these shrink and clip rather than the header's actions.
        */}
        <div
          inert={!pinned}
          className={cn(
            'flex min-w-0 items-center gap-2 overflow-hidden transition-opacity duration-150',
            pinned ? 'opacity-100' : 'hidden',
          )}
        >
          <ToolbarControl metric="objective" label="Objective">
            <Select
              options={goalOptions}
              value={goal}
              onValueChange={(value) => setGoal(value)}
              placeholder="Objective"
              className="border-line bg-field h-8 w-36 rounded-lg text-meta"
            />
          </ToolbarControl>

          {currencyOptions.length > 0 && (
            <ToolbarControl metric="currency" label="Currency">
              <Select
                options={currencyOptions}
                value={currency ?? undefined}
                onValueChange={(value) => setCurrency(value)}
                placeholder="Currency"
                className="border-line bg-field h-8 w-24 rounded-lg text-meta"
              />
            </ToolbarControl>
          )}

          <ToolbarControl metric="evaluationMethod" label="Method">
            <Select
              options={EVALUATION_METHOD_OPTIONS}
              value={evaluationMethod}
              onValueChange={(value) => setEvaluationMethod(value as typeof evaluationMethod)}
              placeholder="Method"
              className="border-line bg-field h-8 w-32 rounded-lg text-meta"
            />
          </ToolbarControl>

          {/* Hidden under Bayesian, matching the card, so the toolbar never offers
              a control that has no effect on what is displayed. */}
          {isFrequentist && (
            <ToolbarControl metric="correctionMethod" label="Correction">
              <Select
                options={CORRECTION_METHOD_OPTIONS}
                value={correctionMethod}
                onValueChange={(value) => setCorrectionMethod(value as typeof correctionMethod)}
                placeholder="Correction"
                className="border-line bg-field h-8 w-40 rounded-lg text-meta"
              />
            </ToolbarControl>
          )}
        </div>
      </TooltipProvider>
    </WorkspaceToolbarControls>
  );
};

type ToolbarControlProps = {
  metric: MetricKey;
  label: string;
  children: React.ReactNode;
};

/**
 * Label beside the control, not above it. Stacked, each label pushed its select below the middle of
 * the toolbar, so the selects sat lower than the breadcrumb and End beside them; one row of
 * 32px controls lines up with both (End is h-8 too).
 *
 * No `text-nano` override. `micro-label`'s 10px is the floor the token layer sets,
 * and these four labels name the controls that decide what every number on the page
 * means — the last place in the product to go below it.
 */
const ToolbarControl = ({ metric, label, children }: ToolbarControlProps) => (
  <label className="flex shrink-0 items-center gap-1.5">
    <MetricLabel metric={metric} className="micro-label">
      {label}
    </MetricLabel>
    {children}
  </label>
);
